// Levels — Photoshop's per-channel input/output mapping with histogram-aware
// controls. Forms the spine of the M4 adjustment surface together with Curves
// and Brightness/Contrast.
//
// Math (per active channel, all values in 0..255):
//
//   t = clamp((pixel - inputBlack) / (inputWhite - inputBlack), 0, 1)
//   t = pow(t, 1 / gamma)                       // midtones
//   out = outputBlack + t * (outputWhite - outputBlack)
//
// The mapping is precomputed into a 256-entry LUT (Uint8ClampedArray) so the
// per-pixel inner loop is exactly one array lookup per channel. Inactive
// channels get the identity LUT.
//
// Alpha is never touched. Selection masking is delegated to
// `applyFilterInPlace` as with every other filter.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type LevelsChannel = "rgb" | "r" | "g" | "b";

export interface LevelsParams {
  channel: LevelsChannel;
  /** 0..255. Pixels at or below this become outputBlack. */
  inputBlack: number;
  /** 0..255. Pixels at or above this become outputWhite. */
  inputWhite: number;
  /** 0.01..9.99. Midtone gamma. 1.0 = linear. */
  gamma: number;
  /** 0..255. Output range floor. */
  outputBlack: number;
  /** 0..255. Output range ceiling. */
  outputWhite: number;
}

const DEFAULT_PARAMS: LevelsParams = {
  channel: "rgb",
  inputBlack: 0,
  inputWhite: 255,
  gamma: 1.0,
  outputBlack: 0,
  outputWhite: 255,
};

/**
 * Build a 256-entry LUT implementing the Levels mapping.
 *
 * Exported for testing.
 */
export function buildLevelsLut(params: LevelsParams): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);
  const ib = clamp255(params.inputBlack);
  const iw = clamp255(params.inputWhite);
  const ob = clamp255(params.outputBlack);
  const ow = clamp255(params.outputWhite);
  // Guard against pathological gamma values. Photoshop's UI clamps to
  // [0.01, 9.99]; we mirror that.
  let g = params.gamma;
  if (!(g > 0) || !isFinite(g)) g = 1.0;
  if (g < 0.01) g = 0.01;
  if (g > 9.99) g = 9.99;
  const invGamma = 1 / g;

  // If the input range collapses to a single value, every pixel <= ib goes to
  // ob and every pixel > ib goes to ow (a hard threshold). This matches what
  // Photoshop does when the user drags the input black and white sliders on
  // top of each other.
  const denom = iw - ib;

  for (let v = 0; v < 256; v++) {
    let out: number;
    if (denom <= 0) {
      out = v <= ib ? ob : ow;
    } else {
      let t = (v - ib) / denom;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
      if (invGamma !== 1) t = Math.pow(t, invGamma);
      out = ob + t * (ow - ob);
    }
    // Uint8ClampedArray takes care of the [0, 255] clamp and rounds to the
    // nearest integer on assignment.
    lut[v] = Math.round(out);
  }
  return lut;
}

function clamp255(v: number): number {
  if (!isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 255) return 255;
  return v;
}

// Identity LUT used for inactive channels. Allocated once so the channel
// selector branch in the inner loop stays as cheap as a property read.
const IDENTITY_LUT: Uint8ClampedArray = (() => {
  const lut = new Uint8ClampedArray(256);
  for (let i = 0; i < 256; i++) lut[i] = i;
  return lut;
})();

export const Levels: Filter<LevelsParams> = {
  id: "levels",
  displayName: "Levels",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: LevelsParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const channel = params.channel;
    const channelLut = buildLevelsLut(params);

    // Pick per-channel LUTs: active channel(s) get the computed LUT, the
    // rest get identity.
    const rLut = channel === "rgb" || channel === "r" ? channelLut : IDENTITY_LUT;
    const gLut = channel === "rgb" || channel === "g" ? channelLut : IDENTITY_LUT;
    const bLut = channel === "rgb" || channel === "b" ? channelLut : IDENTITY_LUT;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      dst[o] = rLut[src[o] ?? 0] ?? 0;
      dst[o + 1] = gLut[src[o + 1] ?? 0] ?? 0;
      dst[o + 2] = bLut[src[o + 2] ?? 0] ?? 0;
      dst[o + 3] = src[o + 3] ?? 0;
    }
    return out;
  },
};
